import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';

import { WindowRef } from '@core/services/window-ref.service';

const POPUP_POLL_MS = 500;
const OPENER_POLL_MS = 1_000;

interface Entry {
  popup: Window;
  closed: Subject<void>;
}

/**
 * The popup windows this window opens, by key, and the window that opened this one.
 *
 * Closing is detected by polling `closed`. An `unload` listener on a popup does not work: it binds
 * to the `about:blank` window `open()` returns, which the popup replaces when it navigates to the
 * app, so it never fires. Polls run outside the Angular zone, where a timer would make the whole
 * app run change detection on every tick.
 */
@Injectable({ providedIn: 'root' })
export class PopupWindows {
  private readonly windowRef = inject(WindowRef);
  private readonly zone = inject(NgZone);
  private readonly popups = new Map<string, Entry>();
  private watcher: ReturnType<typeof setInterval> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopWatching());
  }

  open(
    key: string,
    url: string,
    options: { name?: string; features?: string } = {}
  ): 'opened' | 'focused' | 'blocked' {
    const current = this.popups.get(key);
    if (current && !current.popup.closed) {
      current.popup.focus();
      return 'focused';
    }
    // Closed, but the poll has not noticed yet: a new popup replaces it without a report.
    if (current) this.drop(key, false);
    const popup = this.windowRef.nativeWindow.open(
      url,
      options.name ?? '_blank',
      options.features
    );
    if (!popup) return 'blocked';
    this.popups.set(key, { popup, closed: new Subject() });
    this.watcher ??= this.zone.runOutsideAngular(() =>
      setInterval(() => this.collectClosed(), POPUP_POLL_MS)
    );
    return 'opened';
  }

  isOpen(key: string): boolean {
    const entry = this.popups.get(key);
    return !!entry && !entry.popup.closed;
  }

  /** False when no such popup is open. */
  focus(key: string): boolean {
    if (!this.isOpen(key)) return false;
    this.popups.get(key)!.popup.focus();
    return true;
  }

  /** Emits once when the popup closes, then completes; completes silently when it is replaced. */
  closed(key: string): Observable<void> {
    return this.popups.get(key)?.closed.asObservable() ?? EMPTY;
  }

  /**
   * Closes one popup without reporting it, for a window that is going away. Never every popup: the
   * assistant's window outlives a reload of the tab that opened it.
   */
  close(key: string): void {
    const entry = this.popups.get(key);
    if (!entry) return;
    try {
      if (!entry.popup.closed) entry.popup.close();
    } catch (error) {
      console.warn(`Failed to close popup ${key}:`, error);
    }
    this.drop(key, false);
    if (this.popups.size === 0) this.stopWatching();
  }

  /** Emits once when the window that opened this one is closed, or when there is none. */
  openerClosed(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const timer = this.zone.runOutsideAngular(() =>
        setInterval(() => {
          const opener = this.windowRef.nativeWindow.opener as Window | null;
          if (opener && !opener.closed) return;
          clearInterval(timer);
          this.zone.run(() => {
            subscriber.next();
            subscriber.complete();
          });
        }, OPENER_POLL_MS)
      );
      return () => clearInterval(timer);
    });
  }

  private collectClosed(): void {
    const gone = [...this.popups].filter(([, e]) => e.popup.closed).map(([key]) => key);
    if (gone.length > 0) this.zone.run(() => gone.forEach((key) => this.drop(key, true)));
    if (this.popups.size === 0) this.stopWatching();
  }

  private drop(key: string, report: boolean): void {
    const entry = this.popups.get(key);
    if (!entry) return;
    this.popups.delete(key);
    if (report) entry.closed.next();
    entry.closed.complete();
  }

  private stopWatching(): void {
    if (this.watcher === null) return;
    clearInterval(this.watcher);
    this.watcher = null;
  }
}
